import { AxiosRequestConfig } from 'axios';
import { HttpResponse, ajax } from '../../../../common/ajax';
import { config } from '../../config';
import {
  AddTransactionData,
  BudgetTransactionModel,
} from '../../models/BudgetTransactionModel';
import {
  AddEmployeeData,
  AddressItem,
  EmployeeDetailsModel,
  GroupItemModel,
  UpdateEmployeeData,
} from '../../models/EmployeeDetailsModel';
import { EmployeeItemModel } from '../../models/EmployeeItemModel';
import { StateHistoryModel } from '../../models/StateHistoryModel';

interface IEmployeeAdminService {
  getEmployees: (
    queryString?: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EmployeeItemModel[]>>;
  getEmployeeDetails: (
    employeeId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EmployeeDetailsModel>>;
  getBudgetTransactions: (
    employeeId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<BudgetTransactionModel[]>>;
  getStatusHistory: (
    employeeId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<StateHistoryModel[]>>;
  getGroups: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<GroupItemModel[]>>;
  getAddresses: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<AddressItem[]>>;
  addEmployee: (
    data: AddEmployeeData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  updateEmployee: (
    data: UpdateEmployeeData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  addTransaction: (
    data: AddTransactionData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  downloadBudgetTransactionsReport: (
    employeeId: number,
    fileName: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<null>>;
  approveAccount: (
    employeeId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
}

export const employeeAdminService: IEmployeeAdminService = {
  getEmployees(
    queryString,
    axiosConfig,
  ): Promise<HttpResponse<EmployeeItemModel[]>> {
    return ajax.get<EmployeeItemModel[]>(
      `${config.benefitApiUrl}/odata/employees?${queryString}`,
      axiosConfig,
    );
  },
  getEmployeeDetails(
    employeeId,
    axiosConfig,
  ): Promise<HttpResponse<EmployeeDetailsModel>> {
    return ajax.get<EmployeeDetailsModel>(
      `${config.benefitApiUrl}/employees/${employeeId}/employee-details`,
      axiosConfig,
    );
  },
  getBudgetTransactions(
    employeeId,
    axiosConfig,
  ): Promise<HttpResponse<BudgetTransactionModel[]>> {
    return ajax.get<BudgetTransactionModel[]>(
      `${config.benefitApiUrl}/employees/${employeeId}/budget-transactions`,
      axiosConfig,
    );
  },
  getStatusHistory(
    employeeId,
    axiosConfig,
  ): Promise<HttpResponse<StateHistoryModel[]>> {
    return ajax.get<StateHistoryModel[]>(
      `${config.benefitApiUrl}/employees/${employeeId}/status-history`,
      axiosConfig,
    );
  },
  getGroups(axiosConfig): Promise<HttpResponse<GroupItemModel[]>> {
    return ajax.get<GroupItemModel[]>(
      `${config.benefitApiUrl}/employees/groups`,
      axiosConfig,
    );
  },
  getAddresses(axiosConfig): Promise<HttpResponse<AddressItem[]>> {
    return ajax.get<AddressItem[]>(
      `${config.benefitApiUrl}/employees/addresses`,
      axiosConfig,
    );
  },
  addEmployee(data, axiosConfig): Promise<HttpResponse<boolean>> {
    return ajax.post<boolean>(
      `${config.benefitApiUrl}/add-employee`,
      data,
      axiosConfig,
    );
  },
  updateEmployee(data, axiosConfig): Promise<HttpResponse<boolean>> {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/update-employee`,
      data,
      axiosConfig,
    );
  },
  addTransaction(data, axiosConfig): Promise<HttpResponse<boolean>> {
    return ajax.post<boolean>(
      `${config.benefitApiUrl}/add-transaction`,
      data,
      axiosConfig,
    );
  },
  downloadBudgetTransactionsReport(
    employeeId,
    fileName,
    axiosConfig,
  ): Promise<HttpResponse<null>> {
    return ajax.downloadFile(
      `${config.benefitApiUrl}/download-budget-transactions-report/${employeeId}`,
      fileName,
      'xlsx',
      axiosConfig,
    );
  },
  approveAccount(employeeId, axiosConfig): Promise<HttpResponse<boolean>> {
    return ajax.put(
      `${config.benefitApiUrl}/approve-account/${employeeId}`,
      axiosConfig,
    );
  },
};
